/**
 * Power-gain accounting (owner: rules team; ARCHITECTURE §5 "Stamina & power"). The rules are the ONLY writer that
 * raises `players[i].power` (specials only spend it). Every gain happens inside `rulesPost` between
 * `powerBegin` and `powerFlush`, so the per-tick fold below lives in module scratch that is always reset before
 * use and fully drained in the same call: nothing survives between ticks, rollback/clone never needs it.
 *
 * Event folding: several gains for one player on one tick become ONE POWER_GAIN (summed `amount`, OR-ed
 * `reasons`). Per-yard progress is credited to `power` every tick but announced once per play (at PLAY_END, as
 * PowerReason.YARDS) so a run does not allocate an event per tick. POWER_FULL fires on the tick power reaches max.
 */
import { POWER } from '@hf/shared';
import { PowerReason } from '../enums';
import type { SimEventSink } from '../sink';
import type { SimState } from '../state';

const announced = [0, 0];
const reasons = [0, 0];
const before = [0, 0];

/** Start the tick's fold (call first thing in rulesPost). */
export const powerBegin = (s: SimState): void => {
  for (let i = 0; i < 2; i++) {
    announced[i] = 0;
    reasons[i] = 0;
    before[i] = s.players[i as 0 | 1].power;
  }
};

/** Add `amount` power (capped at POWER.max) and announce it this tick with `reason`. */
export const gainPower = (s: SimState, p: number, amount: number, reason: number): void => {
  if (p !== 0 && p !== 1) return;
  const pl = s.players[p];
  pl.power = Math.min(pl.power + amount, POWER.max);
  announced[p] = announced[p]! + amount;
  reasons[p] = reasons[p]! | reason;
};

/** Input of `gainYardPower` (a scratch object: doubles never cross a per-tick call boundary, so nothing is boxed). */
export const yardGain = { amount: 0.5 };

/** Silent per-yard credit of `yardGain.amount`: added to power now, announced at PLAY_END (`announceYardPower`). */
export const gainYardPower = (s: SimState, p: number): void => {
  const amount = yardGain.amount;
  if ((p !== 0 && p !== 1) || amount <= 0) return;
  const pl = s.players[p];
  // Math.min (not `? max : next`): no HeapNumber boxing on this per-tick path.
  pl.power = Math.min(pl.power + amount, POWER.max);
  const yp = s.rules.yardPower;
  yp[p] = yp[p]! + amount;
};

/** Fold the play's accumulated per-yard power into this tick's POWER_GAIN (PLAY_END). */
export const announceYardPower = (s: SimState): void => {
  const yp = s.rules.yardPower;
  for (let i = 0; i < 2; i++) {
    const a = yp[i] ?? 0;
    if (a > 0) {
      announced[i] = announced[i]! + a;
      reasons[i] = reasons[i]! | PowerReason.YARDS;
    }
    yp[i] = 0;
  }
};

/** Emit the folded POWER_GAIN / POWER_FULL events for this tick (call last in rulesPost). */
export const powerFlush = (s: SimState, ev: SimEventSink): void => {
  if (!ev) return;
  for (let i = 0; i < 2; i++) {
    if (reasons[i] !== 0) {
      ev.push({ type: 'POWER_GAIN', tick: s.tick, actor: i, amount: announced[i]!, reasons: reasons[i]! });
    }
    if (before[i]! < POWER.max && s.players[i as 0 | 1].power >= POWER.max) {
      ev.push({ type: 'POWER_FULL', tick: s.tick, actor: i });
    }
  }
};
